import { CSSProperties, ReactNode, useMemo } from "react";

import { useGetPartnerDetailAPI } from "@/api/partners/usePartnersAPI";
import LokasiIntelligenceLogo from "@/assets/svg/logo.svg?react";
import Satelite from "@/assets/svg/satelite.svg?react";
import { getSubdomain } from "@/utils/location";
import { shouldSkipPartnerCheck } from "@/utils/partner";

import LoginFooter from "./LoginFooter";

const APP_TERMS_URL =
  "https://bvarta.com/good-corporate-governance/#pix-tab-1695295226014-6bcc688d-7f7d";

const APP_SUPPORT_URL = "https://bvarta.com/contact-us/";

const APP_HEADER_TITLE = <>Empower Your Business with LOKASI Intelligence</>;

const APP_HEADER_SUBTITLE = (
  <>
    Unlock actionable insights through advanced geospatial analytics. Visualize{" "}
    trends, optimize operations, and make smarter decisions with ease
  </>
);

// Return the URL only if it's a valid https URL, otherwise null.
const safeHttpsUrl = (value: string): string | null => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
};

export const LoginPageLayout = ({ children }: { children: ReactNode }) => {
  const appSubdomain = getSubdomain();
  const { data: partnerData, isLoading } = useGetPartnerDetailAPI(appSubdomain);

  const appTermsUrl = useMemo(
    () => partnerData?.app_terms_url || APP_TERMS_URL,
    [partnerData?.app_terms_url]
  );
  const appSupportUrl = useMemo(
    () => partnerData?.app_support_url || APP_SUPPORT_URL,
    [partnerData?.app_support_url]
  );
  const appHeaderTitle = useMemo(
    () => partnerData?.app_header_title || APP_HEADER_TITLE,
    [partnerData?.app_header_title]
  );
  const appHeaderSubtitle = useMemo(
    () => partnerData?.app_header_subtitle || APP_HEADER_SUBTITLE,
    [partnerData?.app_header_subtitle]
  );

  // Right panel background: color wins over image when both are set;
  // fall back to the default bg-teal-700 class when both are empty.
  const backgroundStylesRightPanel = useMemo<CSSProperties | undefined>(() => {
    const backgroundColor = partnerData?.app_header_background_color;
    const backgroundUrl = partnerData?.app_header_background_url;

    if (backgroundColor) {
      return { backgroundColor };
    }

    const safeUrl = backgroundUrl ? safeHttpsUrl(backgroundUrl) : null;
    if (safeUrl) {
      return {
        backgroundImage: `url("${safeUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      };
    }

    return undefined;
  }, [
    partnerData?.app_header_background_color,
    partnerData?.app_header_background_url
  ]);

  // Hero visual: the partner logo when provided. Otherwise fall back to the BVT
  // satellite only on the default background — a partner-branded background
  // must not be overlaid with BVT artwork.
  const appHeaderLogo = useMemo(() => {
    if (partnerData?.app_header_logo_url) {
      return (
        <img
          src={partnerData.app_header_logo_url}
          alt={partnerData.app_header_title || "app_header_logo"}
          className="max-h-full max-w-full h-auto object-contain w-48 sm:w-56 md:w-72 lg:w-80 xl:w-96 2xl:w-[30rem]"
        />
      );
    }

    return backgroundStylesRightPanel ? null : (
      <Satelite title="Satellite illustration" className="h-full w-full" />
    );
  }, [
    partnerData?.app_header_logo_url,
    partnerData?.app_header_title,
    backgroundStylesRightPanel
  ]);

  // Avoid rendering BVT defaults before partner branding resolves on partner subdomains.
  if (isLoading && !shouldSkipPartnerCheck(appSubdomain)) {
    return null;
  }

  return (
    <div className="relative flex flex-col min-h-screen gap-5 p-4 sm:grid sm:grid-cols-2 sm:grid-rows-1 sm:h-screen sm:gap-0">
      {/* left */}
      <div className="flex flex-col order-1 w-full gap-5 px-0 sm:order-1 md:px-6 sm:px-6 lg:px-0 sm:grid sm:grid-rows-[auto_1fr_auto] sm:h-full sm:gap-0">
        <div className="w-full max-w-full mx-auto sm:max-w-md sm:pt-[52px]">
          {partnerData?.app_logo_url ? (
            <img
              src={partnerData.app_logo_url}
              alt="app_logo_url"
              className="max-w-[300px] max-h-[120px] w-auto h-auto object-contain"
            />
          ) : (
            <LokasiIntelligenceLogo
              title="Lokasi Intelligence Logo"
              className="h-8 w-auto sm:h-auto"
            />
          )}
        </div>

        <main className="flex flex-col items-center justify-center flex-grow w-full max-w-full mx-auto sm:max-w-[424px]">
          <div className="w-full max-w-full pb-3">{children}</div>
        </main>

        <LoginFooter
          termsUrl={appTermsUrl}
          supportUrl={appSupportUrl}
          className="hidden pb-5 sm:flex opacity-80"
        />
      </div>

      {/* right */}
      <div
        className="relative flex flex-col items-center justify-center order-2 px-6 py-8 sm:py-1 bg-teal-700 rounded-xl sm:order-2 sm:h-full overflow-hidden"
        style={backgroundStylesRightPanel}
      >
        <div
          className="flex flex-col items-center justify-around h-full w-full gap-0 py-4 px-8 lg:py-10 lg:px-20"
          data-testid="login-hero"
        >
          <div className="relative h-[548px] min-h-0 w-[686px] shrink">
            <div className="absolute inset-0 flex items-center justify-center">
              {appHeaderLogo}
            </div>
          </div>
          <div className="min-h-[232px] w-full max-w-[600px] shrink-0">
            <div className="flex flex-col gap-4 text-white">
              <h2 className="text-4xl md:text-5xl font-bold">
                {appHeaderTitle}
              </h2>
              <h3 className="text-sm md:text-base font-semibold">
                {appHeaderSubtitle}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <LoginFooter
        termsUrl={appTermsUrl}
        supportUrl={appSupportUrl}
        className="flex-wrap order-3 pb-4 sm:hidden"
      />
    </div>
  );
};

export default LoginPageLayout;
